// filepath: src/lib/managerMode.js
// Canonical manager-mode state: a manager viewing an owner's account (read-only).
// Single normalized record in storage; stable API + legacy compat helpers.

const KEY = "lwb:managerMode";
const HARDBLOCK = "lw_manager_hardblock";
const EVT = "lwb:managerMode:change";

/* ---------------- storage helpers ---------------- */
function _readRaw() {
  try {
    const s =
      (typeof sessionStorage !== "undefined" && sessionStorage.getItem(KEY)) ||
      (typeof localStorage !== "undefined" && localStorage.getItem(KEY)) ||
      "";
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}
function _writeRaw(obj) {
  const str = JSON.stringify(obj || "");
  try { sessionStorage.setItem(KEY, str); } catch {}
  try { localStorage.setItem(KEY, str); } catch {}
}
function _removeRaw() {
  try { sessionStorage.removeItem(KEY); } catch {}
  try { localStorage.removeItem(KEY); } catch {}
}
function _emitChange() {
  try {
    if (typeof window !== "undefined" && typeof window.CustomEvent === "function") {
      window.dispatchEvent(new CustomEvent(EVT));
    }
  } catch {}
}

/* ---------------- core API ---------------- */
export function sanitizeBoot() {
  try {
    const hb = (typeof sessionStorage !== "undefined" && sessionStorage.getItem(HARDBLOCK)) || null;
    if (hb === "1") return null;

    const rec = _readRaw();
    if (!rec || typeof rec !== "object") return null;

    const { ownerUid, managerUid, grantId, ts } = rec;
    if (!ownerUid || !managerUid) {
      clearManagerMode();
      return null;
    }
    const clean = {
      ownerUid: String(ownerUid),
      managerUid: String(managerUid),
      grantId: grantId ? String(grantId) : null,
      ts: Number(ts) || Date.now(),
      v: 1,
    };
    if (JSON.stringify(clean) !== JSON.stringify(rec)) _writeRaw(clean);
    return clean;
  } catch {
    clearManagerMode();
    return null;
  }
}

export function setManagerMode(ownerUid, managerUid, grantId = null) {
  if (!ownerUid || !managerUid) throw new Error("setManagerMode requires ownerUid and managerUid");
  try { sessionStorage.removeItem(HARDBLOCK); } catch {}
  const rec = { ownerUid: String(ownerUid), managerUid: String(managerUid), grantId: grantId ? String(grantId) : null, ts: Date.now(), v: 1 };
  _writeRaw(rec);
  _emitChange();
  return rec;
}

export function clearManagerMode() {
  _removeRaw();
  _emitChange();
}

export function getManagerOwner() {
  return sanitizeBoot();
}

export { setManagerMode as activate };


export function isReadOnly() {
  return !!sanitizeBoot();
}

/* ---------------- compat / legacy ---------------- */
export function isManaged() { return isReadOnly(); }

export function grantId() {
  const rec = sanitizeBoot();
  return rec?.grantId || null;
}

export function deactivate() { clearManagerMode(); }

export function clearAllManagerTraces() {
  try { clearManagerMode(); } catch {}
  const zap = (store) => {
    if (!store) return;
    for (const k of Object.keys(store)) {
      if (/manager|managed|readonly|grant|owner|lwb:managerMode/i.test(k)) {
        try { store.removeItem(k); } catch {}
      }
    }
  };
  try { zap(sessionStorage); } catch {}
  try { zap(localStorage); } catch {}
  _emitChange();
}

export function setHardblock(flag = true) {
  try {
    if (flag) sessionStorage.setItem(HARDBLOCK, "1");
    else sessionStorage.removeItem(HARDBLOCK);
  } catch {}
}

/* ✅ NEW aliases to stop build errors expecting different names */
export function hardBlock(flag = true) { return setHardblock(flag); }
export function hardblock(flag = true) { return setHardblock(flag); }

export function getManagerUid() {
  const rec = sanitizeBoot();
  return rec?.managerUid || null;
}

export function getOwnerUid() {
  const rec = sanitizeBoot();
  return rec?.ownerUid || null;
}

export function getManagerOwnerUid() {
  return getOwnerUid();
}

export function exitManaged(options = {}) {
  const { hardblock = true } = options || {};
  if (hardblock) setHardblock(true);
  clearAllManagerTraces();
}

/** Subscribe to manager-mode changes. Returns unsubscribe(). */
export function onManagerModeChange(cb) {
  if (typeof window === "undefined" || typeof cb !== "function") return () => {};
  const h1 = () => cb(sanitizeBoot());
  const h2 = (e) => { if (!e || !e.key || e.key === KEY) cb(sanitizeBoot()); };
  window.addEventListener(EVT, h1);
  window.addEventListener("storage", h2);
  try { cb(sanitizeBoot()); } catch {}
  return () => {
    window.removeEventListener(EVT, h1);
    window.removeEventListener("storage", h2);
  };
}

/* ---------------- named default export to satisfy import/no-anonymous-default-export ---------------- */
const api = {
  sanitizeBoot,
  setManagerMode,
  clearManagerMode,
  getManagerOwner,
  isReadOnly,
  isManaged,
  grantId,
  deactivate,
  clearAllManagerTraces,
  setHardblock,
  hardBlock,
  hardblock,
  getManagerUid,
  getOwnerUid,
  getManagerOwnerUid,
  exitManaged,
  onManagerModeChange,
};
export default api;
